import React, { useEffect } from 'react';
import { string } from 'prop-types';
import uniqBy from 'lodash/uniqBy';
import sortBy from 'lodash/sortBy';
import parse from 'html-react-parser';

import useFetch from '../../react-hooks/use-fetch';
import Loading from '../loading';
import Alert from '../../react-components/alert';
import List from './list';
import useQueryString from '../../react-hooks/use-query-string';
import { isUndefined } from 'lodash';

function DeansList({
  stateQueryString,
  countryQueryString,
  errorHeading,
  errorDescription,
  errorNoResults,
  loadingDataLabel,
  fetchUrl,
  showAllLabel,
  selectStateLabel,
  selectCountryLabel,
  otherStateLabel,
  selectCountryOption,
  selectTermOption,
  termQueryString,
  selectTermLabel
}) {
  function termLabel(term) {
    const year = ['20', term.substring(0, 2)].join('');
    let semester = term.substring(term.length - 2);
    switch (semester) {
      case 'FA':
        semester = 'Fall';
        break;
      case 'SP':
        semester = 'Spring';
        break;
    }
    return [semester, ' ', year].join('');
  }
  const [stateQs, setStateQs] = useQueryString(stateQueryString);
  const [countryQs, setCountryQs] = useQueryString(countryQueryString);
  const [termQs, setTermQs] = useQueryString(termQueryString);
  const { response, error, isLoading } = useFetch(fetchUrl);
  const resultListing = response?.data ?? [];
  const terms = resultListing.reduce((acc, { Term }) => {
    const term = Term;
    if (!isUndefined(term)) {
      acc.push({
        label: termLabel(term),
        value: term,
        year: term.substring(0, 2),
        semester: term.substring(term.length - 2)
      });
    }
    return sortBy(uniqBy(acc, 'value'), [(o) => o.value]);
  }, []);
  const countries = resultListing.reduce((acc, { Country, Term }) => {
    const country = Country.trim();
    if (Term === termQs) {
      acc.push({ label: country, value: country });
    }
    return sortBy(uniqBy(acc, 'value'), [(o) => o.value]);
  }, []);
  terms.sort((a, b) => {
    return b.year.localeCompare(a.year) || a.semester - b.semester;
  });
  const states = resultListing.reduce((acc, { State, Country, Term }) => {
    const state = State || otherStateLabel;
    if (Country === countryQs && Term === termQs) {
      acc.push({ label: state, value: state });
    }
    return sortBy(uniqBy(acc, 'value'), [(o) => o.value]);
  }, []);
  const defaultTermOption = {
    label: selectTermOption,
    value: ''
  };
  const defaultCountryOption = {
    label: selectCountryOption,
    value: ''
  };
  const defaultStateOption = { label: showAllLabel, value: showAllLabel };

  if (isLoading) return <Loading label={loadingDataLabel} />;

  if (error)
    return (
      <Alert
        heading={errorHeading}
        description={errorDescription}
        type="error"
      />
    );

  return resultListing?.length ? (
    <List
      results={resultListing}
      countries={[defaultCountryOption, ...countries]}
      states={[defaultStateOption, ...states]}
      selectStateLabel={selectStateLabel}
      selectCountryLabel={selectCountryLabel}
      stateQs={stateQs}
      setStateQs={setStateQs}
      countryQs={countryQs}
      setCountryQs={setCountryQs}
      showAllLabel={showAllLabel}
      otherStateLabel={otherStateLabel}
      selectTermLabel={selectTermLabel}
      terms={[defaultTermOption, ...terms]}
      termQs={termQs}
      setTermQs={setTermQs}
    />
  ) : (
    parse(errorNoResults)
  );
}

DeansList.propTypes = {
  stateQueryString: string.isRequired,
  countryQueryString: string.isRequired,
  errorHeading: string.isRequired,
  errorDescription: string.isRequired,
  errorNoResults: string.isRequired,
  loadingDataLabel: string.isRequired,
  fetchUrl: string.isRequired,
  showAllLabel: string.isRequired,
  selectStateLabel: string.isRequired,
  otherStateLabel: string.isRequired,
  selectCountryLabel: string.isRequired,
  selectCountryOption: string.isRequired,
  selectTermOption: string.isRequired,
  termQueryString: string.isRequired,
  selectTermLabel: string.isRequired
};

export default DeansList;
